import * as THREE from 'three'
import {
  type ExportOptions,
  exportToPly,
  exportToSplat,
} from '@/plugins/gsplat/export/SplatExporter'
import { GSplatResolveSH } from '@/plugins/gsplat/gpu/GSplatResolveSH'
import { GSplatDirector } from '@/plugins/gsplat/lod/GSplatDirector'
import type { GSplatManager } from '@/plugins/gsplat/lod/GSplatManager'
import { GSplatPlacement } from '@/plugins/gsplat/lod/GSplatPlacement'
import { GSplatResource } from '@/plugins/gsplat/lod/GSplatResource'
import { Layer } from '@/plugins/gsplat/lod/Layer'
import { CameraComponent, LayerComposition } from '@/plugins/gsplat/lod/LayerComposition'
import { GaussianSplatMaterial } from '@/plugins/gsplat/materials/GaussianSplatMaterial'
import { type SorterOptions, SplatSorter } from '@/plugins/gsplat/sorting/SplatSorter'
import { type Ellipsoid, EllipsoidRaycast } from '@/plugins/gsplat/spatial/EllipsoidRaycast'
import { type CropBox, type CropSphere, SplatCropper } from '@/plugins/gsplat/tools/SplatCropper'
import { getRendererViewportDrawingBufferSize } from '@/plugins/gsplat/utils/getRendererViewportSize'
import { GSPLAT_SORT_DIRECTION_THRESHOLD, GSPLAT_SORT_POSITION_THRESHOLD } from '@/shared/constants'
import { getLoggerManager } from '@/shared/logging'
import type { GSplatShaderEffectLike } from '@/shared/types/gsplat'
import { beginDisposeOnce } from '@/shared/utils/disposeOnce'
import type { ColorSpaceManager } from './ColorSpaceManager'
import { CompressedSplatData } from './CompressedSplatData'
import { GaussianSplatGeometry } from './GaussianSplatGeometry'
import { SogsData } from './SogsData'
import { SplatData } from './SplatData'

const log = getLoggerManager().getLogger('gsplat:core')

/**
 * 扩展 Three.js 事件映射以支持自定义事件
 */
declare module 'three' {
  interface Object3DEventMap {
    sortComplete: { count: number; timeMs: number }
  }
}

/**
 * GaussianSplatMesh 配置选项
 */
export interface GaussianSplatMeshOptions {
  /** 是否启用深度写入 (默认: false) */
  depthWrite?: boolean
  /** 是否启用深度测试 (默认: true) */
  depthTest?: boolean
  /** 是否透明 (默认: true) */
  transparent?: boolean
  /** 是否启用球谐光照 (默认: true) */
  enableSH?: boolean
  /** 最大球谐阶数 0-3 (默认: 3) */
  maxSHBands?: number
  /** 是否启用自动排序 (默认: true) */
  enableSorting?: boolean
  /** 排序器配置选项 */
  sortingOptions?: SorterOptions
  /**
   * 自定义包围盒用于视锥裁剪
   *
   * 如果提供，将使用此包围盒替代自动计算的包围盒。
   * 这在以下场景中很有用：
   * - 数据有异常点导致包围盒过大
   * - 想要手动裁剪特定区域
   * - 需要精确控制裁剪边界
   *
   * @example
   * ```typescript
   * const customBox = new THREE.Box3(
   *   new THREE.Vector3(-10, -10, -10),
   *   new THREE.Vector3(10, 10, 10)
   * );
   * const mesh = new GaussianSplatMesh(splatData, { customAabb });
   * ```
   */
  customAabb?: THREE.Box3

  /**
   * 是否启用SOGS GPU打包优化（仅对SOGS格式有效）
   *
   * GPU打包将多个源纹理合并为紧凑格式，优势：
   * - 减少纹理采样次数（6-7次 → 2-3次）
   * - 提升GPU解压性能 10-20x
   * - 内存布局更紧凑
   *
   * 注意：需要WebGL2支持和足够的GPU内存
   *
   * @default true (SOGS格式自动启用)
   */
  enableGpuPacking?: boolean

  /**
   * GPU打包后是否释放源纹理（仅在enableGpuPacking=true时有效）
   *
   * 启用后可进一步节省内存，但无法回退到CPU解压
   *
   * @default false
   */
  gpuPackingMinimalMemory?: boolean

  /**
   * 是否启用统一LOD系统（PlayCanvas兼容）
   *
   * 启用后将使用GSplatDirector/GSplatManager进行LOD管理，
   * 提供更强大的多实例、多相机支持
   *
   * 注意：与enableLOD互斥，优先使用enableUnifiedLOD
   *
   * @default false
   */
  enableUnifiedLOD?: boolean

  /**
   * 统一LOD资源路径（例如: 'model.sog'）
   *
   * 仅在 enableUnifiedLOD=true 时有效
   */
  unifiedLodResourcePath?: string
}

/**
 * 相机状态（多相机支持）
 */
interface CameraState {
  /** 相机对象 */
  camera: THREE.Camera
  /** 该相机的排序纹理 */
  orderTexture: THREE.DataTexture
  /** 该相机的排序器 */
  sorter: SplatSorter
  /** 排序版本（用于检测更新） */
  sortVersion: number
}

/**
 * 排序器数据适配器接口
 * 定义 SplatSorter 所需的最小数据契约
 */
interface ISorterDataAdapter {
  /** Splat 总数 */
  numSplats: number
  /** 获取中心点坐标数组（格式：[x1,y1,z1, x2,y2,z2, ...]） */
  getCenters(): Float32Array
}

/**
 * 高斯点云网格对象
 *
 * 整合 GaussianSplatGeometry 和 GaussianSplatMaterial，
 * 提供完整的高斯点云渲染能力。
 *
 * 使用方式：
 * ```typescript
 * const loader = new SplatLoader();
 * const splatData = await loader.load('model.ply');
 * const mesh = new GaussianSplatMesh(splatData);
 * scene.add(mesh);
 * ```
 *
 * 多相机支持（可选）：
 * ```typescript
 * mesh.addCamera(camera2); // 添加第二个相机
 * mesh.addCamera(camera3); // 添加第三个相机
 * // 渲染时自动根据当前相机选择对应的排序结果
 * ```
 */
export class GaussianSplatMesh extends THREE.Mesh<GaussianSplatGeometry, GaussianSplatMaterial> {
  /** 原始 Splat 数据引用 */
  readonly splatData: SplatData | CompressedSplatData | SogsData

  /** 排序器（单相机模式） */
  private sorter: SplatSorter | null = null

  /** 多相机状态映射（启用多相机时使用） */
  private cameraStates: Map<THREE.Camera, CameraState> | null = null

  /** 色彩空间管理器（可选） */
  private colorSpaceManager: ColorSpaceManager | null = null

  /** PlayCanvas 风格的最终渲染 shader effect */
  private _shaderEffect: GSplatShaderEffectLike | null = null

  /** 裁剪器（懒加载） */
  private cropper: SplatCropper | null = null

  /** 统一LOD总控（如果启用） */
  private director: GSplatDirector | null = null

  /** 统一LOD的Layer Composition */
  private composition: LayerComposition | null = null

  /** 统一LOD的Layer实例 */
  private layer: Layer | null = null

  /** 统一LOD的Placement实例 */
  private placement: GSplatPlacement | null = null

  /** 统一LOD的GSplatResource实例 */
  private gsplatResource: GSplatResource | null = null

  /** 高质量 SH 解析器（SOGS 格式专用） */
  private resolveSH: GSplatResolveSH | null = null

  /** SH labels 纹理（从 SOGS packed 数据提取或保留原始） */
  private shLabelsTexture: THREE.DataTexture | null = null

  /** SH centroids 纹理（SOGS SH palette） */
  private shCentroidsTexture: THREE.DataTexture | null = null

  /** 排序防抖：上一次相机位置缓存 */
  private lastSortCameraPosition = new THREE.Vector3(Infinity, Infinity, Infinity)

  /** 排序防抖：上一次相机方向缓存 */
  private lastSortCameraDirection = new THREE.Vector3(Infinity, Infinity, Infinity)

  /** 排序防抖：位置变化阈值 */
  private static readonly SORT_POSITION_THRESHOLD = GSPLAT_SORT_POSITION_THRESHOLD

  /** 排序防抖：方向变化阈值（点积差值） */
  private static readonly SORT_DIRECTION_THRESHOLD = GSPLAT_SORT_DIRECTION_THRESHOLD

  // GPU 打包相关标志
  /** 是否需要执行 GPU 打包（延迟到首次渲染） */
  private _needsGpuPacking: boolean = false

  /** GPU 打包后是否释放源纹理（最小内存模式） */
  private _gpuPackingMinimalMemory: boolean = false

  // 统一 LOD 相关标志
  /** 是否需要初始化统一 LOD（延迟到首次渲染） */
  private _needsUnifiedLodInit: boolean = false

  /** 统一 LOD 资源路径（用于延迟加载） */
  private _unifiedLodResourcePath: string | null = null

  /** 当前 Placement 的资源路径（用于资源管理） */
  private _placementResourcePath: string | null = null

  /** 当前 WebGL viewport 尺寸（绘制缓冲区像素） */
  private _viewportSize = new THREE.Vector2()

  /**
   * 构造函数
   * @param splatData PLY 文件解析出的 Splat 数据（支持压缩格式和 SOGS 格式）
   * @param options 渲染配置选项
   */
  constructor(
    splatData: SplatData | CompressedSplatData | SogsData,
    options: GaussianSplatMeshOptions = {}
  ) {
    // 检测数据格式
    const isCompressed = splatData instanceof CompressedSplatData
    const isSogs = splatData instanceof SogsData

    // 如果是压缩格式，需要临时解压用于 Geometry（GPU 上传）
    let geometryData: SplatData

    if (isSogs) {
      const sogs = splatData as SogsData

      // 临时解压用于纹理上传
      geometryData = GaussianSplatMesh.decompressFromSogs(sogs)
    } else if (isCompressed) {
      const compressed = splatData as CompressedSplatData

      // 临时解压用于纹理上传
      geometryData = GaussianSplatMesh.decompressForGeometry(compressed)
    } else {
      geometryData = splatData as SplatData
    }

    // 创建 Geometry 和 Material
    const geometry = new GaussianSplatGeometry(geometryData)

    // 关键修复：如果数据没有 SH，则禁用 SH 以避免 usampler2D 采样器未绑定错误
    // 当 shader 启用 SH_BANDS 但 SH 纹理为 null 时，WebGL 会报告
    // "Mismatch between texture format and sampler type" 错误
    const actualSHBands = geometry.shBands
    const effectiveEnableSH = actualSHBands > 0 ? (options.enableSH ?? true) : false
    const effectiveMaxSHBands = actualSHBands > 0 ? (options.maxSHBands ?? actualSHBands) : 0

    const material = new GaussianSplatMaterial({
      depthWrite: options.depthWrite,
      depthTest: options.depthTest,
      transparent: options.transparent,
      enableSH: effectiveEnableSH,
      maxSHBands: effectiveMaxSHBands,
    })

    // super() 调用必须在第一行之前或所有初始化属性之前
    super(geometry, material)

    // 保留原始数据（可能是压缩格式）
    this.splatData = splatData

    // SOGS GPU 打包优化（批次8功能）
    // 注意：必须在 super() 之后调用，因为需要访问渲染器上下文
    if (isSogs && options.enableGpuPacking !== false) {
      // GPU 打包需要在首次渲染时执行（需要 WebGLRenderer 上下文）
      // 这里先标记，在 onBeforeRender 中延迟执行
      this._needsGpuPacking = true
      this._gpuPackingMinimalMemory = options.gpuPackingMinimalMemory || false
    }

    // 设置材质纹理和元数据
    material.setDataTextures(
      geometry.dataTextureA,
      geometry.dataTextureB,
      geometry.colorTexture,
      geometry.orderTexture
    )
    material.setMetadata(splatData.numSplats, geometry.dataWidth, geometry.orderWidth)

    // 设置 SH 纹理（如果有）
    if (geometry.shBands > 0 && geometry.shTextures.length > 0) {
      material.setSHTextures(geometry.shTextures, geometry.shBands)
    }

    // 设置包围盒（使用自定义 AABB 或自动计算）
    if (options.customAabb) {
      // 使用用户提供的自定义包围盒
      this.geometry.boundingBox = options.customAabb.clone()
    } else {
      // 自动计算包围盒
      const aabb = new THREE.Box3()
      splatData.calcAabb(aabb)
      this.geometry.boundingBox = aabb
    }

    // 重要：设置 boundingSphere（Three.js 视锥剔除使用 boundingSphere）
    this.updateBoundingSphereFromBox()

    // 初始化排序器
    if (options.enableSorting !== false) {
      // 获取 centers 数据（支持两种格式）
      const centers = splatData.getCenters()

      // 创建数据适配器
      const sorterData: ISorterDataAdapter = {
        numSplats: splatData.numSplats,
        getCenters: () => centers,
      }

      // 注意：使用 ISorterDataAdapter 接口，SplatSorter 已支持该类型
      this.sorter = new SplatSorter(sorterData, geometry.orderTexture, {
        ...options.sortingOptions,
        onSortComplete: (data) => {
          // 使用 Worker 返回的可见数量更新 batched draw instanceCount
          this.geometry.setVisibleSplatCount(data.count)

          // 转发排序完成事件
          this.dispatchEvent({ type: 'sortComplete', ...data })
        },
      })
    }

    // 初始化统一LOD系统（如果启用）
    if (options.enableUnifiedLOD && options.unifiedLodResourcePath) {
      // 注意：统一LOD的完整初始化需要渲染器，延迟到 onBeforeRender
      this._needsUnifiedLodInit = true
      this._unifiedLodResourcePath = options.unifiedLodResourcePath
    }
  }

  /**
   * 初始化统一LOD系统
   *
   * @param renderer - WebGL 渲染器
   * @param camera - 相机对象
   * @param resourcePath - 资源路径
   */
  private initUnifiedLOD(
    renderer: THREE.WebGLRenderer,
    camera: THREE.Camera,
    resourcePath: string
  ): void {
    // 创建 Director
    this.director = new GSplatDirector(renderer)

    // 创建 Layer Composition
    this.composition = new LayerComposition()

    // 创建 Layer
    this.layer = new Layer('main-layer')
    this.layer.enabled = true
    this.composition.addLayer(this.layer)

    // 创建 GSplatResource 包装器
    // 注意：splatData 可能是 SplatData、CompressedSplatData 或 SogsData
    // GSplatResource 需要 SplatData 或 SogsData
    let resourceData: SplatData | SogsData
    if (this.splatData instanceof CompressedSplatData) {
      // CompressedSplatData 需要解压
      resourceData = GaussianSplatMesh.decompressForGeometry(this.splatData)
    } else if (this.splatData instanceof SogsData) {
      resourceData = this.splatData
    } else {
      resourceData = this.splatData as SplatData
    }
    const resource = new GSplatResource(resourceData)
    this.gsplatResource = resource // 保存引用以便后续清理

    // 创建 Placement（代表一个 Splat 实例）
    this.placement = new GSplatPlacement(resource, this)

    // 设置 Placement 的 AABB（重要：用于 LOD 评估）
    this.placement.aabb = resource.aabb

    // 存储资源路径供后续加载使用
    this._placementResourcePath = resourcePath

    // 添加 Placement 到 Layer
    this.layer.addGSplatPlacement(this.placement)

    // 注册相机
    const cameraComponent = new CameraComponent(camera, [this.layer.id])
    this.composition.addCamera(cameraComponent)
  }

  /**
   * 从 SOGS 格式解压用于 Geometry 创建
   * 对标 PlayCanvas GSplatSogsData.decompress()
   */
  private static decompressFromSogs(sogs: SogsData): SplatData {
    const numSplats = sogs.numSplats
    const shBands = sogs.shBands

    // 分配存储空间
    const x = new Float32Array(numSplats)
    const y = new Float32Array(numSplats)
    const z = new Float32Array(numSplats)
    const rot_0 = new Float32Array(numSplats)
    const rot_1 = new Float32Array(numSplats)
    const rot_2 = new Float32Array(numSplats)
    const rot_3 = new Float32Array(numSplats)
    const scale_0 = new Float32Array(numSplats)
    const scale_1 = new Float32Array(numSplats)
    const scale_2 = new Float32Array(numSplats)
    const f_dc_0 = new Float32Array(numSplats)
    const f_dc_1 = new Float32Array(numSplats)
    const f_dc_2 = new Float32Array(numSplats)
    const opacity = new Float32Array(numSplats)

    // SH 数据（如果有）
    // PlayCanvas: shBands 1 → 3 系数, 2 → 8 系数, 3 → 15 系数 (每通道)
    const shCoeffsPerChannel = shBands > 0 ? [3, 8, 15][shBands - 1] : 0
    const fRest: Float32Array[] = []
    for (let i = 0; i < shCoeffsPerChannel * 3; i++) {
      fRest.push(new Float32Array(numSplats))
    }

    // 使用迭代器批量解压
    const iter = sogs.createIterator()
    for (let i = 0; i < numSplats; i++) {
      const splat = iter.read(i)

      x[i] = splat.position[0]
      y[i] = splat.position[1]
      z[i] = splat.position[2]

      rot_0[i] = splat.rotation[0]
      rot_1[i] = splat.rotation[1]
      rot_2[i] = splat.rotation[2]
      rot_3[i] = splat.rotation[3]

      scale_0[i] = splat.scale[0]
      scale_1[i] = splat.scale[1]
      scale_2[i] = splat.scale[2]

      f_dc_0[i] = splat.color[0]
      f_dc_1[i] = splat.color[1]
      f_dc_2[i] = splat.color[2]

      opacity[i] = splat.opacity

      // 复制 SH 系数（如果有）
      // splat.sh 布局: [r0,r1,...r14, g0,g1,...g14, b0,b1,...b14]
      // fRest 布局: f_rest_0 ~ f_rest_44，顺序为 [r0,g0,b0, r1,g1,b1, ...]
      if (splat.sh && shBands > 0) {
        for (let c = 0; c < shCoeffsPerChannel; c++) {
          // PlayCanvas splat.sh 布局: sh[channel * 15 + coeff]
          fRest[c * 3 + 0][i] = splat.sh[0 * 15 + c] // R 通道
          fRest[c * 3 + 1][i] = splat.sh[1 * 15 + c] // G 通道
          fRest[c * 3 + 2][i] = splat.sh[2 * 15 + c] // B 通道
        }
      }
    }

    // 构建 SplatData properties
    const properties: Array<{ name: string; type: string; storage: Float32Array }> = [
      { name: 'x', type: 'float', storage: x },
      { name: 'y', type: 'float', storage: y },
      { name: 'z', type: 'float', storage: z },
      { name: 'f_dc_0', type: 'float', storage: f_dc_0 },
      { name: 'f_dc_1', type: 'float', storage: f_dc_1 },
      { name: 'f_dc_2', type: 'float', storage: f_dc_2 },
    ]

    // 添加 SH 系数（如果有）
    // 顺序：f_rest_0, f_rest_1, ..., f_rest_{n-1}
    for (let i = 0; i < fRest.length; i++) {
      properties.push({ name: `f_rest_${i}`, type: 'float', storage: fRest[i] })
    }

    // 添加剩余属性
    properties.push(
      { name: 'opacity', type: 'float', storage: opacity },
      { name: 'scale_0', type: 'float', storage: scale_0 },
      { name: 'scale_1', type: 'float', storage: scale_1 },
      { name: 'scale_2', type: 'float', storage: scale_2 },
      { name: 'rot_0', type: 'float', storage: rot_0 },
      { name: 'rot_1', type: 'float', storage: rot_1 },
      { name: 'rot_2', type: 'float', storage: rot_2 },
      { name: 'rot_3', type: 'float', storage: rot_3 }
    )

    // 构建 SplatData
    const splatData = new SplatData()
    splatData.numSplats = numSplats
    splatData.elements = [
      {
        name: 'vertex',
        count: numSplats,
        properties,
      },
    ]

    return splatData
  }

  /**
   * 临时解压压缩数据用于 Geometry 创建
   * 这是一个简化版本，只用于纹理上传
   */
  private static decompressForGeometry(compressed: CompressedSplatData): SplatData {
    const numSplats = compressed.numSplats
    const shBands = compressed.shBands

    // 分配存储空间
    const x = new Float32Array(numSplats)
    const y = new Float32Array(numSplats)
    const z = new Float32Array(numSplats)
    const rot_0 = new Float32Array(numSplats)
    const rot_1 = new Float32Array(numSplats)
    const rot_2 = new Float32Array(numSplats)
    const rot_3 = new Float32Array(numSplats)
    const scale_0 = new Float32Array(numSplats)
    const scale_1 = new Float32Array(numSplats)
    const scale_2 = new Float32Array(numSplats)
    const f_dc_0 = new Float32Array(numSplats)
    const f_dc_1 = new Float32Array(numSplats)
    const f_dc_2 = new Float32Array(numSplats)
    const opacity = new Float32Array(numSplats)

    // SH数据
    const shCoeffsPerChannel = shBands > 0 ? [3, 8, 15][shBands - 1] : 0
    const fRest: Float32Array[] = []
    for (let i = 0; i < shCoeffsPerChannel * 3; i++) {
      fRest.push(new Float32Array(numSplats))
    }

    // 使用迭代器批量解压
    const iter = compressed.createIterator()
    for (let i = 0; i < numSplats; i++) {
      const splat = iter.read(i)

      x[i] = splat.position[0]
      y[i] = splat.position[1]
      z[i] = splat.position[2]

      rot_0[i] = splat.rotation[0]
      rot_1[i] = splat.rotation[1]
      rot_2[i] = splat.rotation[2]
      rot_3[i] = splat.rotation[3]

      scale_0[i] = splat.scale[0]
      scale_1[i] = splat.scale[1]
      scale_2[i] = splat.scale[2]

      f_dc_0[i] = splat.color[0]
      f_dc_1[i] = splat.color[1]
      f_dc_2[i] = splat.color[2]

      opacity[i] = splat.opacity

      if (splat.sh) {
        for (let k = 0; k < fRest.length; k++) {
          fRest[k][i] = splat.sh[k]
        }
      }
    }

    // 构建SplatData
    const splatData = new SplatData()
    splatData.numSplats = numSplats

    const properties: Array<{ name: string; type: string; storage: Float32Array }> = [
      { name: 'x', type: 'float', storage: x },
      { name: 'y', type: 'float', storage: y },
      { name: 'z', type: 'float', storage: z },
      { name: 'f_dc_0', type: 'float', storage: f_dc_0 },
      { name: 'f_dc_1', type: 'float', storage: f_dc_1 },
      { name: 'f_dc_2', type: 'float', storage: f_dc_2 },
      { name: 'opacity', type: 'float', storage: opacity },
      { name: 'scale_0', type: 'float', storage: scale_0 },
      { name: 'scale_1', type: 'float', storage: scale_1 },
      { name: 'scale_2', type: 'float', storage: scale_2 },
      { name: 'rot_0', type: 'float', storage: rot_0 },
      { name: 'rot_1', type: 'float', storage: rot_1 },
      { name: 'rot_2', type: 'float', storage: rot_2 },
      { name: 'rot_3', type: 'float', storage: rot_3 },
    ]

    for (let i = 0; i < fRest.length; i++) {
      properties.push({
        name: `f_rest_${i}`,
        type: 'float',
        storage: fRest[i],
      })
    }

    splatData.elements = [
      {
        name: 'vertex',
        count: numSplats,
        properties,
      },
    ]

    return splatData
  }

  /**
   * 渲染前回调
   * 自动更新视口尺寸到材质 uniform，并触发排序
   */
  onBeforeRender = (
    renderer: THREE.WebGLRenderer,
    _scene: THREE.Scene,
    camera: THREE.Camera
  ): void => {
    // GPU 打包（批次8功能）- 延迟到首次渲染执行
    if (this._needsGpuPacking && this.splatData instanceof SogsData) {
      const sogs = this.splatData as SogsData
      const minimalMemory = this._gpuPackingMinimalMemory

      const packed = sogs.packGpuMemory(renderer, minimalMemory)

      if (packed) {
        // 替换材质纹理为打包后的紧凑纹理
        this.material.setDataTextures(
          packed.dataTextureA,
          packed.dataTextureB,
          packed.colorTexture,
          this.geometry.orderTexture
        )
        // 关键：更新 dataWidth 以匹配打包后的纹理尺寸
        this.material.uniforms.dataWidth.value = packed.width

        // 关键修复：GPU 打包后切换到标准 PLY 格式，必须移除 SOGS defines
        // 因为打包后的纹理格式是 RGBA32UI + RGBA16F（与标准 PLY 一致）
        // 而 GSPLAT_SOGS_DATA 模式使用的是 sampler2D（RGBA8）
        if (this.material.defines.GSPLAT_SOGS_DATA) {
          this.material.defines.GSPLAT_SOGS_DATA = undefined
          this.material.defines.GSPLAT_SOGS_V2 = undefined
          this.material.needsUpdate = true
        }

        // ====== 高质量 SH 初始化（SOGS 格式专用）======
        // 检查是否有 SH 数据
        const shPaletteInfo = sogs.getShPaletteInfo()

        if (shPaletteInfo && sogs.shLabels && sogs.shCentroids) {
          // 1. 创建 sh_labels 纹理
          this.shLabelsTexture = new THREE.DataTexture(
            sogs.shLabels,
            packed.width,
            packed.height,
            THREE.RGBAFormat,
            THREE.UnsignedByteType
          )
          this.shLabelsTexture.minFilter = THREE.NearestFilter
          this.shLabelsTexture.magFilter = THREE.NearestFilter
          this.shLabelsTexture.needsUpdate = true

          // 2. 预处理 shCentroids（V2 格式需要 codebook 查找和归一化）
          // 参考 PlayCanvas gsplatSogsReorderSh.js 的 resolveCodebook 逻辑
          const shNMeta = sogs.meta.shN
          let processedCentroids: Uint8Array
          let shNMins: number
          let shNMaxs: number

          if (sogs.meta.version === 2 && shNMeta?.codebook) {
            // V2 格式：用 codebook 将索引转换为归一化值
            const codebook = shNMeta.codebook
            const cbMin = codebook[0]
            const cbMax = codebook[codebook.length - 1]
            const cbRange = cbMax - cbMin

            processedCentroids = new Uint8Array(sogs.shCentroids.length)
            for (let i = 0; i < sogs.shCentroids.length; i += 4) {
              // RGB 通道存储索引，Alpha 通道通常是 255
              for (let c = 0; c < 3; c++) {
                const idx = sogs.shCentroids[i + c]
                const value = codebook[idx] ?? 0
                // 归一化到 [0, 1]
                const normalized = cbRange !== 0 ? (value - cbMin) / cbRange : 0
                // 转换为 uint8
                processedCentroids[i + c] = Math.round(Math.max(0, Math.min(1, normalized)) * 255)
              }
              processedCentroids[i + 3] = sogs.shCentroids[i + 3] // Alpha 保持不变
            }

            // 归一化后，mins=0, maxs=1（因为 resolveCodebook 已经归一化了）
            // 但实际 SH 值范围需要用原始 codebook 的 min/max
            shNMins = cbMin
            shNMaxs = cbMax
          } else {
            // V1 格式：直接使用，用 mins/maxs 反归一化
            processedCentroids = sogs.shCentroids
            shNMins = shNMeta?.mins ?? 0
            shNMaxs = shNMeta?.maxs ?? 1
          }

          // 3. 创建 sh_centroids 纹理（使用预处理后的数据）
          this.shCentroidsTexture = new THREE.DataTexture(
            processedCentroids,
            shPaletteInfo.width,
            shPaletteInfo.height,
            THREE.RGBAFormat,
            THREE.UnsignedByteType
          )
          this.shCentroidsTexture.minFilter = THREE.NearestFilter
          this.shCentroidsTexture.magFilter = THREE.NearestFilter
          this.shCentroidsTexture.needsUpdate = true

          // 4. 初始化 GSplatResolveSH
          this.resolveSH = new GSplatResolveSH(renderer, shPaletteInfo.shBands)

          // 5. 设置 centroids 纹理
          this.resolveSH.setCentroids(this.shCentroidsTexture, shNMins, shNMaxs)

          // 6. 启用高质量 SH 模式
          this.material.defines.USE_HIGH_QUALITY_SH = 1
          this.material.needsUpdate = true

          // 7. 绑定 SH 纹理到材质
          this.material.uniforms.sh_labels.value = this.shLabelsTexture
          this.material.uniforms.sh_result.value = this.resolveSH.texture

          if (this.material.uniforms.shN_mins) {
            this.material.uniforms.shN_mins.value = new THREE.Vector4(
              shNMins,
              shNMins,
              shNMins,
              shNMins
            )
          }
          if (this.material.uniforms.shN_maxs) {
            this.material.uniforms.shN_maxs.value = new THREE.Vector4(
              shNMaxs,
              shNMaxs,
              shNMaxs,
              shNMaxs
            )
          }

          // 8. 禁用普通 SH（因为高质量 SH 已启用）
          this.material.defines.SH_BANDS = 0
          this.material.uniforms.shBands.value = 0
        }
      }

      // 清除标记，只执行一次
      this._needsGpuPacking = false
      this._gpuPackingMinimalMemory = false
    }

    // 高质量 SH 更新（每帧调用）
    if (this.resolveSH) {
      this.resolveSH.render(camera, this.matrixWorld)
    }

    // 统一LOD初始化（延迟执行）
    if (this._needsUnifiedLodInit) {
      const resourcePath = this._unifiedLodResourcePath
      if (resourcePath) {
        this.initUnifiedLOD(renderer, camera, resourcePath)
      }
      this._needsUnifiedLodInit = false
      this._unifiedLodResourcePath = null
    }

    // 统一LOD更新（如果启用）
    if (this.director && this.composition) {
      // Placement 的节点变换会自动同步（因为 node 就是 this）

      // 更新 Director（执行 LOD 评估和排序）
      this.director.updateForRender(this.composition)

      // 获取 Manager 并应用到材质
      const manager = this.director.getManager(camera, this.layer!)
      if (manager) {
        // 将 WorkBuffer 的数据绑定到材质
        // manager.workBuffer 包含了 LOD 筛选后的 Splat 数据
        this.material.setUnifiedLODTextures(
          manager.colorTexture,
          manager.splatTexture0,
          manager.splatTexture1,
          manager.orderTexture,
          manager.textureSize
        )

        // 绑定高质量 SH 纹理（如果 Manager 启用了高质量 SH）
        if (manager.isHighQualitySHActive && manager.shResultTexture) {
          // Unified WorkBuffer 颜色已经在 copy pass 中写入；当前没有 WorkBuffer 布局的 sh_labels。
          this.material.setHighQualitySHTexture(manager.shResultTexture, null)
        } else {
          // 禁用高质量 SH
          this.material.setHighQualitySHTexture(null)
        }

        const visibleSplatCount = manager.visibleSplatCount
        this.geometry.setVisibleSplatCount(visibleSplatCount)
        this.material.setMetadata(visibleSplatCount, manager.textureSize, manager.textureSize)
      }
    }

    // 多视口渲染时需要使用当前 view 的绘制缓冲区尺寸，而不是整块 canvas 尺寸。
    const size = getRendererViewportDrawingBufferSize(renderer, this._viewportSize)
    this.material.setViewport(size.x, size.y)

    // 更新相机参数（PlayCanvas 兼容）
    this.material.setCameraParams(camera)

    // 色彩空间自动配置（如果有管理器）
    if (this.colorSpaceManager) {
      this.colorSpaceManager.configureMaterial(this.material)
    } else {
      // 没有管理器时，材质自己处理 auto 模式
      this.material.updateGammaMode(renderer)
    }

    // 触发排序（如果启用）
    // 传入 mesh 的 matrixWorld，确保排序在模型空间进行（与渲染一致）
    // 添加防抖：只有相机位置/方向变化超过阈值才触发排序
    const shouldSort = this.checkCameraChanged(camera)

    if (this.cameraStates) {
      // 多相机模式：根据当前相机选择对应的排序器
      const state = this.cameraStates.get(camera)
      if (state) {
        if (shouldSort) {
          state.sorter.setCamera(camera, this.matrixWorld)
        }
        // 更新材质使用该相机的 orderTexture
        this.material.setDataTextures(
          this.geometry.dataTextureA,
          this.geometry.dataTextureB,
          this.geometry.colorTexture,
          state.orderTexture
        )
      } else {
        // 当前相机未注册，回退到默认排序器（如果有）
        if (this.sorter && shouldSort) {
          this.sorter.setCamera(camera, this.matrixWorld)
        }
        // 恢复默认 orderTexture
        this.material.setDataTextures(
          this.geometry.dataTextureA,
          this.geometry.dataTextureB,
          this.geometry.colorTexture,
          this.geometry.orderTexture
        )
      }
    } else if (this.sorter && shouldSort) {
      // 单相机模式：使用默认排序器
      this.sorter.setCamera(camera, this.matrixWorld)
    }
  }

  /**
   * 检查相机是否发生足够的变化需要重新排序
   * PlayCanvas 风格防抖策略
   *
   * @param camera 当前相机
   * @returns 是否需要触发排序
   */
  private checkCameraChanged(camera: THREE.Camera): boolean {
    const pos = camera.position
    const dir = camera.getWorldDirection(new THREE.Vector3())

    // 位置变化检测
    const posDelta = pos.distanceToSquared(this.lastSortCameraPosition)
    const posThreshold =
      GaussianSplatMesh.SORT_POSITION_THRESHOLD * GaussianSplatMesh.SORT_POSITION_THRESHOLD

    // 方向变化检测（使用点积）
    const dirDot = dir.dot(this.lastSortCameraDirection)
    const dirChanged = dirDot < 1.0 - GaussianSplatMesh.SORT_DIRECTION_THRESHOLD

    if (posDelta > posThreshold || dirChanged) {
      // 更新缓存
      this.lastSortCameraPosition.copy(pos)
      this.lastSortCameraDirection.copy(dir)
      return true
    }

    return false
  }

  /**
   * Raycaster 交互支持，使用精确的椭球体相交检测。
   *
   * @param raycaster - 外部传入的 Raycaster
   * @param intersects - 输出数组，命中结果追加其中
   */
  raycast(raycaster: THREE.Raycaster, intersects: THREE.Intersection[]): void {
    if (!this.splatData) {
      return
    }

    // PlayCanvas 策略：遍历所有 splat（不使用八叉树加速）
    const candidates = Array.from({ length: this.splatData.numSplats }, (_, i) => i)

    if (candidates.length === 0) {
      return
    }

    // 获取 splat 数据（支持压缩格式和 SOGS 格式）
    let x: Float32Array, y: Float32Array, z: Float32Array
    let scale0: Float32Array, scale1: Float32Array, scale2: Float32Array
    let rot0: Float32Array | null = null
    let rot1: Float32Array | null = null
    let rot2: Float32Array | null = null
    let rot3: Float32Array | null = null

    if (this.splatData instanceof CompressedSplatData || this.splatData instanceof SogsData) {
      // 压缩格式或 SOGS 格式：按需解压（性能较低，但节省内存）
      // TODO: 考虑缓存解压后的位置数据
      const iter = this.splatData.createIterator()
      const numSplats = this.splatData.numSplats

      x = new Float32Array(numSplats)
      y = new Float32Array(numSplats)
      z = new Float32Array(numSplats)
      scale0 = new Float32Array(numSplats)
      scale1 = new Float32Array(numSplats)
      scale2 = new Float32Array(numSplats)
      rot0 = new Float32Array(numSplats)
      rot1 = new Float32Array(numSplats)
      rot2 = new Float32Array(numSplats)
      rot3 = new Float32Array(numSplats)

      for (let i = 0; i < numSplats; i++) {
        const splat = iter.read(i)
        x[i] = splat.position[0]
        y[i] = splat.position[1]
        z[i] = splat.position[2]
        scale0[i] = splat.scale[0]
        scale1[i] = splat.scale[1]
        scale2[i] = splat.scale[2]
        rot0[i] = splat.rotation[0]
        rot1[i] = splat.rotation[1]
        rot2[i] = splat.rotation[2]
        rot3[i] = splat.rotation[3]
      }
    } else {
      // 标准格式：直接访问
      x = this.splatData.getProp('x') as Float32Array
      y = this.splatData.getProp('y') as Float32Array
      z = this.splatData.getProp('z') as Float32Array
      scale0 = this.splatData.getProp('scale_0') as Float32Array
      scale1 = this.splatData.getProp('scale_1') as Float32Array
      scale2 = this.splatData.getProp('scale_2') as Float32Array
      rot0 = this.splatData.getProp('rot_0') as Float32Array
      rot1 = this.splatData.getProp('rot_1') as Float32Array
      rot2 = this.splatData.getProp('rot_2') as Float32Array
      rot3 = this.splatData.getProp('rot_3') as Float32Array
    }

    if (!x || !y || !z || !scale0 || !scale1 || !scale2) {
      return
    }

    // 是否有旋转数据
    const hasRotation = rot0 && rot1 && rot2 && rot3

    // 复用临时对象
    const center = new THREE.Vector3()
    const worldRay = new THREE.Ray()
    const tempResults: Array<{ distance: number; point: THREE.Vector3; index: number }> = []

    // 将射线转换到模型空间
    const invMatrix = new THREE.Matrix4().copy(this.matrixWorld).invert()
    worldRay.copy(raycaster.ray).applyMatrix4(invMatrix)

    // 第一遍：使用包围球快速剔除
    const roughCandidates: number[] = []
    for (const idx of candidates) {
      center.set(x[idx], y[idx], z[idx])

      // 计算包围球半径
      const maxRadius =
        Math.max(Math.exp(scale0[idx]), Math.exp(scale1[idx]), Math.exp(scale2[idx])) * 2 // 放宽阈值

      if (EllipsoidRaycast.roughIntersect(worldRay, center, maxRadius)) {
        roughCandidates.push(idx)
      }
    }

    // 第二遍：使用精确的椭球体相交测试
    for (const idx of roughCandidates) {
      center.set(x[idx], y[idx], z[idx])

      // 构建椭球体
      const ellipsoid: Ellipsoid = hasRotation
        ? EllipsoidRaycast.fromSplatData(
            center,
            scale0[idx],
            scale1[idx],
            scale2[idx],
            rot0?.[idx],
            rot1?.[idx],
            rot2?.[idx],
            rot3?.[idx]
          )
        : {
            center: center.clone(),
            radii: new THREE.Vector3(
              Math.exp(scale0[idx]),
              Math.exp(scale1[idx]),
              Math.exp(scale2[idx])
            ),
            rotation: new THREE.Quaternion(0, 0, 0, 1), // 单位四元数
          }

      // 精确相交测试
      const result = EllipsoidRaycast.intersect(worldRay, ellipsoid, 1.5)

      if (result.intersects) {
        // 变换回世界空间
        const worldPoint = result.point.clone().applyMatrix4(this.matrixWorld)
        const worldDistance = worldPoint.distanceTo(raycaster.ray.origin)

        // 检查距离范围
        if (worldDistance >= raycaster.near && worldDistance <= raycaster.far) {
          tempResults.push({
            distance: worldDistance,
            point: worldPoint,
            index: idx,
          })
        }
      }
    }

    // 按距离排序并添加到结果
    tempResults.sort((a, b) => a.distance - b.distance)

    for (const result of tempResults) {
      intersects.push({
        distance: result.distance,
        point: result.point,
        object: this,
        index: result.index,
      } as THREE.Intersection)
    }
  }

  /**
   * 释放所有资源
   * 包括 Geometry、Material、排序器和数据纹理
   */
  setShaderEffect(effect: GSplatShaderEffectLike | null): boolean {
    if (this._shaderEffect === effect) {
      return effect ? effect.enable(this.material) : true
    }

    this._shaderEffect?.disable()
    this._shaderEffect = effect

    if (!effect) {
      this.material.clearShaderEffect()
      return true
    }

    return effect.enable(this.material)
  }

  clearShaderEffect(): boolean {
    return this.setShaderEffect(null)
  }

  restartShaderEffect(): boolean {
    return this._shaderEffect?.restart() ?? false
  }

  getShaderEffect(): GSplatShaderEffectLike | null {
    return this._shaderEffect
  }

  updateShaderEffect(delta: number): void {
    this._shaderEffect?.update(delta)
  }

  dispose(): void {
    if (!beginDisposeOnce(this)) return

    // Three r186 起 Object3D 才提供 dispose()，仅用于派发 dispose 事件；旧 peer 无该方法，必须可选调用。
    super.dispose?.()
    this._shaderEffect?.destroy()
    this._shaderEffect = null
    this.sorter?.dispose()

    // 释放所有相机状态的排序器和纹理
    if (this.cameraStates) {
      for (const [, state] of this.cameraStates) {
        state.sorter.dispose()
        state.orderTexture.dispose()
      }
      this.cameraStates.clear()
    }

    // 释放高质量 SH 资源
    this.resolveSH?.dispose()
    this.resolveSH = null
    this.shLabelsTexture?.dispose()
    this.shLabelsTexture = null
    this.shCentroidsTexture?.dispose()
    this.shCentroidsTexture = null

    this.director?.destroy()
    this.director = null
    this.composition = null
    this.layer = null
    this.placement = null
    this.gsplatResource?.dispose()
    this.gsplatResource = null
    this.cropper = null
    this.geometry.dispose()
    this.material.dispose()
  }

  // ============================================================
  // 多相机支持
  // ============================================================

  /**
   * 添加相机到多相机渲染系统
   *
   * 启用多相机支持后，每个相机将拥有独立的排序纹理和排序器。
   * 这对于以下场景很有用：
   * - 分屏渲染（如 VR）
   * - 小地图 / 缩略图预览
   * - 多视角同步渲染
   *
   * @param camera 要添加的相机
   * @param options 可选的排序器配置
   * @returns 是否成功添加（已存在则返回 false）
   *
   * @example
   * ```typescript
   * const mesh = new GaussianSplatMesh(splatData);
   * mesh.addCamera(mainCamera);  // 主相机
   * mesh.addCamera(mirrorCamera); // 镜像相机
   * ```
   */
  addCamera(camera: THREE.Camera, options?: SorterOptions): boolean {
    // 如果是第一次添加相机，需要初始化多相机模式
    if (!this.cameraStates) {
      this.cameraStates = new Map()

      // 如果已有默认排序器，将其迁移到第一个相机
      if (this.sorter) {
        const defaultState: CameraState = {
          camera,
          orderTexture: this.geometry.orderTexture,
          sorter: this.sorter,
          sortVersion: 0,
        }
        this.cameraStates.set(camera, defaultState)
        this.sorter = null // 清空默认排序器引用
        return true
      }
    }

    // 检查相机是否已存在
    if (this.cameraStates.has(camera)) {
      log.warn('[ThreeGS] 相机已存在于多相机系统中')
      return false
    }

    // 创建新的 orderTexture（复制原始纹理的尺寸）
    const width = this.geometry.orderWidth
    const height = Math.ceil(this.splatData.numSplats / width)
    const orderData = new Uint32Array(width * height)

    // 初始化为顺序索引
    for (let i = 0; i < this.splatData.numSplats; i++) {
      orderData[i] = i
    }

    const orderTexture = new THREE.DataTexture(
      orderData,
      width,
      height,
      THREE.RedIntegerFormat,
      THREE.UnsignedIntType
    )
    orderTexture.internalFormat = 'R32UI' // 明确指定内部格式
    orderTexture.minFilter = THREE.NearestFilter
    orderTexture.magFilter = THREE.NearestFilter
    orderTexture.needsUpdate = true

    // 创建新的排序器
    const centers = this.splatData.getCenters()

    // 创建数据适配器
    const sorterData: ISorterDataAdapter = {
      numSplats: this.splatData.numSplats,
      getCenters: () => centers,
    }

    // 注意：使用 ISorterDataAdapter 接口，SplatSorter 已支持该类型
    const sorter = new SplatSorter(sorterData, orderTexture, {
      ...options,
      onSortComplete: (data) => {
        // 更新该相机的实例计数
        if (data.count > 0) {
          // 注意：这里不能直接修改 geometry.instanceCount
          // 因为 instanceCount 是全局的，多相机共享
          // 所以我们只能在渲染时通过 orderTexture 控制可见性
        }
      },
    })

    // 创建相机状态
    const state: CameraState = {
      camera,
      orderTexture,
      sorter,
      sortVersion: 0,
    }

    this.cameraStates.set(camera, state)
    return true
  }

  /**
   * 移除相机
   *
   * @param camera 要移除的相机
   * @returns 是否成功移除（不存在则返回 false）
   */
  removeCamera(camera: THREE.Camera): boolean {
    if (!this.cameraStates || !this.cameraStates.has(camera)) {
      log.warn('[ThreeGS] 相机不在多相机系统中')
      return false
    }

    const state = this.cameraStates.get(camera)!
    state.sorter.dispose()
    state.orderTexture.dispose()
    this.cameraStates.delete(camera)

    // 如果只剩一个相机，可以考虑退回单相机模式
    if (this.cameraStates.size === 0) {
      this.cameraStates = null
    }

    return true
  }

  /**
   * 获取所有已注册的相机
   *
   * @returns 相机数组（单相机模式返回空数组）
   */
  getCameras(): THREE.Camera[] {
    if (!this.cameraStates) return []
    return Array.from(this.cameraStates.keys())
  }

  /**
   * 检查是否启用了多相机模式。
   *
   * @returns 存在 per-camera 排序状态时返回 true
   */
  isMultiCameraEnabled(): boolean {
    return this.cameraStates !== null
  }

  /**
   * 获取指定相机的排序统计信息。
   *
   * @param camera - 目标相机
   * @returns 当前版本号和 splat 数量；非多相机或未记录返回 null
   */
  getCameraSortStats(camera: THREE.Camera): { version: number; count: number } | null {
    if (!this.cameraStates) return null
    const state = this.cameraStates.get(camera)
    if (!state) return null

    return {
      version: state.sortVersion,
      count: this.geometry.visibleSplatCount,
    }
  }

  // ============================================================
  // 色彩空间管理
  // ============================================================

  /**
   * 设置色彩空间管理器
   * @param manager 色彩空间管理器实例
   */
  setColorSpaceManager(manager: ColorSpaceManager): void {
    this.colorSpaceManager = manager
    // 立即应用配置
    if (manager) {
      manager.configureMaterial(this.material)
    }
  }

  /**
   * 获取色彩空间管理器
   * @returns 当前的色彩空间管理器（如果有）
   */
  getColorSpaceManager(): ColorSpaceManager | null {
    return this.colorSpaceManager
  }

  // ============================================================
  // 排序控制
  // ============================================================

  /**
   * 更新排序顺序
   * @param sortedIndices 排序后的索引数组
   */
  updateOrder(sortedIndices: Uint32Array): void {
    this.geometry.updateOrder(sortedIndices)
  }

  // 调试渲染模式暂时屏蔽
  // /**
  //  * 设置渲染模式
  //  * @param mode 渲染模式
  //  */
  // setRenderMode(mode: DebugRenderMode): void {
  //   this.material.setRenderMode(mode);
  // }
  //
  // /**
  //  * 获取当前渲染模式
  //  */
  // getRenderMode(): DebugRenderMode {
  //   return this.material.getRenderMode();
  // }
  //
  // /**
  //  * 渲染模式属性（getter/setter）
  //  */
  // get renderMode(): DebugRenderMode {
  //   return this.material.getRenderMode();
  // }
  //
  // set renderMode(mode: DebugRenderMode) {
  //   this.material.setRenderMode(mode);
  // }
  //
  // /**
  //  * 设置深度可视化范围
  //  * @param near 近平面距离
  //  * @param far 远平面距离
  //  */
  // setDepthRange(near: number, far: number): void {
  //   this.material.setDepthRange(near, far);
  // }

  // ============================================================
  // 裁剪功能
  // ============================================================

  /**
   * 获取裁剪器实例（懒加载）。
   *
   * @returns 首次调用时按当前数据构建裁剪器，之后返回同一实例
   */
  getCropper(): SplatCropper {
    if (!this.cropper) {
      // SplatCropper 需要 SplatData，如果是压缩格式或 SOGS 格式则先解压
      let dataForCropper: SplatData

      if (this.splatData instanceof SogsData) {
        dataForCropper = GaussianSplatMesh.decompressFromSogs(this.splatData as SogsData)
      } else if (this.splatData instanceof CompressedSplatData) {
        dataForCropper = GaussianSplatMesh.decompressForGeometry(
          this.splatData as CompressedSplatData
        )
      } else {
        dataForCropper = this.splatData as SplatData
      }

      this.cropper = new SplatCropper(dataForCropper)
    }
    return this.cropper
  }

  /**
   * 按包围盒裁剪
   * @param box 包围盒
   * @param invert 是否反转（false=删除盒外，true=删除盒内）
   * @returns 新删除的splat数量
   */
  cropByBox(box: CropBox, invert?: boolean): number {
    return this.getCropper().cropByBox(box, invert)
  }

  /**
   * 按球体裁剪
   * @param sphere 球体
   * @param invert 是否反转（false=删除球外，true=删除球内）
   * @returns 新删除的splat数量
   */
  cropBySphere(sphere: CropSphere, invert?: boolean): number {
    return this.getCropper().cropBySphere(sphere, invert)
  }

  /**
   * 按平面裁剪
   * @param plane 裁剪平面
   * @param keepPositiveSide 是否保留正半空间
   * @returns 新删除的splat数量
   */
  cropByPlane(plane: THREE.Plane, keepPositiveSide?: boolean): number {
    return this.getCropper().cropByPlane(plane, keepPositiveSide)
  }

  /**
   * 按索引删除splat
   * @param indices 要删除的索引数组
   */
  deleteByIndices(indices: number[]): void {
    this.getCropper().deleteByIndices(indices)
  }

  /**
   * 重置裁剪（恢复所有splat）
   */
  resetCrop(): void {
    this.cropper?.reset()
  }

  /**
   * 获取裁剪统计。
   *
   * @returns 含 total/deleted/remaining 字段；无裁剪器时 total 与 remaining 均为 numSplats
   */
  getCropStats(): { total: number; deleted: number; remaining: number } {
    return (
      this.cropper?.getStats() || {
        total: this.splatData.numSplats,
        deleted: 0,
        remaining: this.splatData.numSplats,
      }
    )
  }

  // ============================================================
  // 导出功能
  // ============================================================

  /**
   * 导出为PLY或.splat格式
   *
   * @param options 导出选项
   * @returns Blob对象，可用于下载或保存
   *
   * @example
   * ```typescript
   * // 导出为PLY（包含裁剪结果）
   * const blob = await mesh.export({ format: 'ply', maxSHBands: 3 });
   * const url = URL.createObjectURL(blob);
   * const a = document.createElement('a');
   * a.href = url;
   * a.download = 'model.ply';
   * a.click();
   * ```
   */
  async export(options?: ExportOptions & { format?: 'ply' | 'splat' }): Promise<Blob> {
    // 如果是压缩格式或 SOGS 格式，需要先解压
    let dataToExport: SplatData

    if (this.splatData instanceof SogsData) {
      dataToExport = GaussianSplatMesh.decompressFromSogs(this.splatData as SogsData)
    } else if (this.splatData instanceof CompressedSplatData) {
      dataToExport = GaussianSplatMesh.decompressForGeometry(this.splatData as CompressedSplatData)
    } else {
      dataToExport = this.splatData as SplatData
    }

    const exportOptions: ExportOptions = {
      ...options,
      deletedMask: this.cropper?.getDeletedMask(),
    }

    if (options?.format === 'splat') {
      return exportToSplat(dataToExport, exportOptions)
    }

    return exportToPly(dataToExport, exportOptions)
  }

  /**
   * 导出为 PLY 格式。
   *
   * @param options - 导出选项，透传至底层 exporter
   * @returns 包含 PLY 字节流的 Blob
   */
  async exportPly(options?: ExportOptions): Promise<Blob> {
    return this.export({ ...options, format: 'ply' })
  }

  /**
   * 导出为 .splat 格式。
   *
   * @param options - 导出选项
   * @returns 包含 .splat 字节流的 Blob
   */
  async exportSplat(options?: ExportOptions): Promise<Blob> {
    return this.export({ ...options, format: 'splat' })
  }

  // ============================================================
  // 自定义包围盒管理
  // ============================================================

  /**
   * 设置自定义包围盒
   *
   * @param aabb 自定义包围盒，如果为 null 则重新自动计算
   *
   * @example
   * ```typescript
   * // 设置自定义包围盒
   * const customBox = new THREE.Box3(
   *   new THREE.Vector3(-5, -5, -5),
   *   new THREE.Vector3(5, 5, 5)
   * );
   * mesh.setCustomAabb(customBox);
   *
   * // 恢复自动计算
   * mesh.setCustomAabb(null);
   * ```
   */
  setCustomAabb(aabb: THREE.Box3 | null): void {
    if (aabb) {
      // 使用自定义包围盒
      this.geometry.boundingBox = aabb.clone()
    } else {
      // 重新计算包围盒
      const newAabb = new THREE.Box3()
      this.splatData.calcAabb(newAabb)
      this.geometry.boundingBox = newAabb
    }

    // 同步更新 boundingSphere
    this.updateBoundingSphereFromBox()
  }

  /**
   * 从 boundingBox 更新 boundingSphere
   *
   * Three.js 视锥剔除使用 boundingSphere，而不是 boundingBox。
   * 必须确保 boundingSphere 正确设置，否则会出现模型消失的问题。
   *
   * @remarks
   * 这个方法会根据当前的 boundingBox 计算一个包围球：
   * - 中心 = boundingBox 的中心
   * - 半径 = 中心到 boundingBox 角点的距离
   */
  updateBoundingSphereFromBox(): void {
    const box = this.geometry.boundingBox
    if (!box) {
      log.warn('[ThreeGS] 无法更新 boundingSphere：boundingBox 未设置')
      return
    }

    // 计算包围球
    const center = box.getCenter(new THREE.Vector3())
    const size = box.getSize(new THREE.Vector3())
    // 半径 = 对角线长度的一半
    const radius = size.length() / 2

    // 设置或创建 boundingSphere
    if (!this.geometry.boundingSphere) {
      this.geometry.boundingSphere = new THREE.Sphere(center, radius)
    } else {
      this.geometry.boundingSphere.center.copy(center)
      this.geometry.boundingSphere.radius = radius
    }
  }

  /**
   * 获取当前包围盒
   *
   * @returns 当前使用的包围盒（可能是自定义的或自动计算的）
   */
  getAabb(): THREE.Box3 | null {
    return this.geometry.boundingBox
  }

  /**
   * 扩展包围盒
   *
   * 在当前包围盒的基础上扩展指定的边距
   *
   * @param margin 扩展边距（正数扩大，负数缩小）
   *
   * @example
   * ```typescript
   * // 扩大包围盒 10%
   * mesh.expandAabb(10);
   *
   * // 缩小包围盒 5%
   * mesh.expandAabb(-5);
   * ```
   */
  expandAabb(margin: number): void {
    if (!this.geometry.boundingBox) {
      log.warn('[ThreeGS] 无法扩展包围盒：boundingBox 未设置')
      return
    }

    const box = this.geometry.boundingBox
    const center = box.getCenter(new THREE.Vector3())
    const size = box.getSize(new THREE.Vector3())

    // 计算新尺寸
    const newSize = size.multiplyScalar(1 + margin / 100)

    // 从中心和新尺寸重建包围盒
    box.setFromCenterAndSize(center, newSize)

    // 同步更新 boundingSphere
    this.updateBoundingSphereFromBox()
  }

  /**
   * 设置包围盒边距
   *
   * 在自动计算的包围盒基础上添加固定边距
   *
   * @param padding 各方向的边距（xyz）
   *
   * @example
   * ```typescript
   * // 各方向添加 1.0 单位边距
   * mesh.setAabbPadding(new THREE.Vector3(1, 1, 1));
   *
   * // 只在 y 方向添加边距
   * mesh.setAabbPadding(new THREE.Vector3(0, 2, 0));
   * ```
   */
  setAabbPadding(padding: THREE.Vector3): void {
    // 重新计算原始包围盒
    const aabb = new THREE.Box3()
    this.splatData.calcAabb(aabb)

    // 添加边距
    aabb.min.sub(padding)
    aabb.max.add(padding)

    this.geometry.boundingBox = aabb

    // 同步更新 boundingSphere
    this.updateBoundingSphereFromBox()
  }

  // ============================================================
  // LOD 管理
  // ============================================================

  /**
   * 获取统一 LOD Director（如果启用）。
   *
   * @returns 当前 Director；未启用 LOD 时返回 null
   */
  getDirector(): GSplatDirector | null {
    return this.director
  }

  /**
   * 获取统一LOD Manager（如果启用）
   *
   * @param camera - 相机对象
   * @returns GSplatManager 或 null
   */
  getUnifiedLODManager(camera: THREE.Camera): GSplatManager | null {
    if (!this.director || !this.layer) return null
    return this.director.getManager(camera, this.layer)
  }

  /**
   * 获取统一 LOD Placement（如果启用）。
   *
   * @returns 当前 Placement；未启用 LOD 时返回 null
   */
  getPlacement(): GSplatPlacement | null {
    return this.placement
  }

  /**
   * 更新统一LOD资源路径
   *
   * @param resourcePath - 新的资源路径
   */
  updateUnifiedLODResource(resourcePath: string): void {
    if (this.placement) {
      // 存储资源路径供后续加载使用
      this._placementResourcePath = resourcePath
      if (this.layer) {
        this.layer.gsplatPlacementsDirty = true
      }
    }
  }

  // ============================================================
  // 调试工具
  // ============================================================

  /**
   * 触发排序并输出调试日志
   * @param camera 当前相机
   */
  debugSort(camera: THREE.Camera): void {
    if (!this.sorter) {
      log.warn('[ThreeGS Debug] 排序器未启用')
      return
    }

    // 触发排序
    this.sorter.setCamera(camera, this.matrixWorld)
  }

  /**
   * 获取调试信息（字符串拼接，避免打印对象）
   *
   * @param camera 当前相机
   * @returns 调试信息字符串
   */
  getDebugInfo(camera: THREE.Camera): string {
    const lines: string[] = []
    lines.push('======== GaussianSplatMesh Debug ========')

    // Mesh 基本状态
    lines.push(`[Mesh] visible=${this.visible}, frustumCulled=${this.frustumCulled}`)
    lines.push(`[Mesh] name="${this.name}"`)

    // 世界矩阵
    const worldPos = new THREE.Vector3()
    const worldScale = new THREE.Vector3()
    const worldQuat = new THREE.Quaternion()
    this.matrixWorld.decompose(worldPos, worldQuat, worldScale)
    lines.push(
      `[Mesh] worldPos=(${worldPos.x.toFixed(3)}, ${worldPos.y.toFixed(3)}, ${worldPos.z.toFixed(3)})`
    )
    lines.push(
      `[Mesh] worldScale=(${worldScale.x.toFixed(3)}, ${worldScale.y.toFixed(3)}, ${worldScale.z.toFixed(3)})`
    )

    // Geometry 状态
    const geom = this.geometry
    lines.push(`[Geometry] visibleSplatCount=${geom.visibleSplatCount}`)
    lines.push(`[Geometry] batchInstanceCount=${geom.instanceCount}`)
    lines.push(`[Geometry] dataWidth=${geom.dataWidth}, dataHeight=${geom.dataHeight}`)
    lines.push(`[Geometry] orderWidth=${geom.orderWidth}`)

    // 包围盒
    const bbox = geom.boundingBox
    if (bbox) {
      lines.push(
        `[Geometry] boundingBox.min=(${bbox.min.x.toFixed(3)}, ${bbox.min.y.toFixed(3)}, ${bbox.min.z.toFixed(3)})`
      )
      lines.push(
        `[Geometry] boundingBox.max=(${bbox.max.x.toFixed(3)}, ${bbox.max.y.toFixed(3)}, ${bbox.max.z.toFixed(3)})`
      )

      // 包围盒尺寸
      const size = bbox.getSize(new THREE.Vector3())
      lines.push(
        `[Geometry] boundingBox.size=(${size.x.toFixed(3)}, ${size.y.toFixed(3)}, ${size.z.toFixed(3)})`
      )

      // 检查包围盒是否有效
      const isValidBox =
        bbox.min.x <= bbox.max.x && bbox.min.y <= bbox.max.y && bbox.min.z <= bbox.max.z
      lines.push(`[Geometry] boundingBox.valid=${isValidBox}`)
    } else {
      lines.push('[Geometry] boundingBox=null')
    }

    // 包围球（Three.js 视锥剔除使用这个）
    const bsphere = geom.boundingSphere
    if (bsphere) {
      lines.push(
        `[Geometry] boundingSphere.center=(${bsphere.center.x.toFixed(3)}, ${bsphere.center.y.toFixed(3)}, ${bsphere.center.z.toFixed(3)})`
      )
      lines.push(`[Geometry] boundingSphere.radius=${bsphere.radius.toFixed(3)}`)
    } else {
      lines.push('[Geometry] boundingSphere=null (PROBLEM: frustum culling will fail!)')
    }

    // 数据状态
    lines.push(`[Data] numSplats=${this.splatData.numSplats}`)

    // 排序器状态
    if (this.sorter) {
      lines.push('[Sorter] enabled=true')
    } else {
      lines.push('[Sorter] enabled=false')
    }

    // 排序防抖状态
    lines.push(
      `[SortDebounce] lastPos=(${this.lastSortCameraPosition.x.toFixed(3)}, ${this.lastSortCameraPosition.y.toFixed(3)}, ${this.lastSortCameraPosition.z.toFixed(3)})`
    )
    lines.push(
      `[SortDebounce] lastDir=(${this.lastSortCameraDirection.x.toFixed(3)}, ${this.lastSortCameraDirection.y.toFixed(3)}, ${this.lastSortCameraDirection.z.toFixed(3)})`
    )

    // 相机状态
    const camPos = camera.position
    const camDir = camera.getWorldDirection(new THREE.Vector3())
    lines.push(
      `[Camera] position=(${camPos.x.toFixed(3)}, ${camPos.y.toFixed(3)}, ${camPos.z.toFixed(3)})`
    )
    lines.push(
      `[Camera] direction=(${camDir.x.toFixed(3)}, ${camDir.y.toFixed(3)}, ${camDir.z.toFixed(3)})`
    )

    // 检测相机变化
    const posDelta = camPos.distanceToSquared(this.lastSortCameraPosition)
    const posThreshold =
      GaussianSplatMesh.SORT_POSITION_THRESHOLD * GaussianSplatMesh.SORT_POSITION_THRESHOLD
    const dirDot = camDir.dot(this.lastSortCameraDirection)
    const dirChanged = dirDot < 1.0 - GaussianSplatMesh.SORT_DIRECTION_THRESHOLD
    lines.push(
      `[Camera] positionDeltaSquared=${posDelta.toFixed(6)}, threshold=${posThreshold.toFixed(6)}`
    )
    lines.push(`[Camera] directionDot=${dirDot.toFixed(6)}, dirChanged=${dirChanged}`)

    // 视锥裁剪检测
    if (bbox && camera instanceof THREE.PerspectiveCamera) {
      // 计算世界空间的包围盒
      const worldBbox = bbox.clone().applyMatrix4(this.matrixWorld)
      const frustum = new THREE.Frustum()
      const projScreenMatrix = new THREE.Matrix4()
      projScreenMatrix.multiplyMatrices(camera.projectionMatrix, camera.matrixWorldInverse)
      frustum.setFromProjectionMatrix(projScreenMatrix)

      const inFrustum = frustum.intersectsBox(worldBbox)
      lines.push(`[Frustum] meshInFrustum=${inFrustum}`)
      lines.push(
        `[Frustum] worldBbox.min=(${worldBbox.min.x.toFixed(3)}, ${worldBbox.min.y.toFixed(3)}, ${worldBbox.min.z.toFixed(3)})`
      )
      lines.push(
        `[Frustum] worldBbox.max=(${worldBbox.max.x.toFixed(3)}, ${worldBbox.max.y.toFixed(3)}, ${worldBbox.max.z.toFixed(3)})`
      )
    }

    // 多相机状态
    if (this.cameraStates) {
      lines.push(`[MultiCamera] enabled=true, cameraCount=${this.cameraStates.size}`)
      const hasCurrentCamera = this.cameraStates.has(camera)
      lines.push(`[MultiCamera] currentCameraRegistered=${hasCurrentCamera}`)
    } else {
      lines.push('[MultiCamera] enabled=false')
    }

    // 统一LOD状态
    if (this.director) {
      lines.push('[UnifiedLOD] enabled=true')
      lines.push(`[UnifiedLOD] gsplatCount=${this.director.gsplatCount}`)
    } else {
      lines.push('[UnifiedLOD] enabled=false')
    }

    lines.push('=========================================')
    return lines.join('\n')
  }
}
